/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';

import styles from './Notification.style';
import NotificationBanner from 'src/shared/ui/component/messaging/NotificationBanner';

type Props = {
  timeout?: number,
  layoutClass?: string,
  withWarning?: boolean,
  withError?: boolean,
  href?: string,
  media?: React.Node,
  handleClick?: () => void,
  handleDismiss?: () => void,
  children: React.Node,
};

const useStyles = createUseStyles(styles);

let timeoutId: number;

const Notification = ({
  layoutClass,
  withWarning,
  withError,
  href,
  media,
  timeout,
  handleClick,
  handleDismiss,
  children,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isHidden, setIsHidden] = React.useState(true);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    open();

    // If no timeout is passed, we show the banner indefinetly
    if (timeout) {
      timeoutId = window.setTimeout(onDismiss, timeout);
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onDismiss = () => close();

  const onTransitionEnd = ({ target }: TransitionEvent) => {
    const { current: rootElt } = rootRef;

    if (target === rootElt && isHidden && handleDismiss) {
      handleDismiss();
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Functions
   |----------------------------------------------------------------------------
   */

  const open = () => {
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setIsHidden(false)),
    );
  };

  const close = () => {
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setIsHidden(true)),
    );
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const rootClasses = classNames(classes.root, {
    [classes['root-isHidden']]: isHidden,
  });

  /*
   |----------------------------------------------------------------------------
   | Refs
   |----------------------------------------------------------------------------
   */

  const rootRef = React.createRef();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <div
      className={rootClasses}
      onTransitionEnd={onTransitionEnd}
      ref={rootRef}
    >
      <NotificationBanner
        layoutClass={layoutClass}
        withWarning={withWarning}
        withError={withError}
        href={href}
        media={media}
        handleClick={handleClick}
        handleDismiss={onDismiss}
      >
        {children}
      </NotificationBanner>
    </div>
  );
};

export default compose(React.memo)(Notification);
